import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { cloneDeep } from "lodash";
import { GlobalStyles, Box } from "@mui/material";

import { OnBoardingProvider } from "../../context/onBoardingContext";
import { useAppState } from "../../context/StateContext";
import useActionSyncUser from "../../hooks/useSyncUser";
import useActionOnBoarding from "../../hooks/useActionOnBoarding";
import useOnBoardingContext from "../../hooks/useOnBoardingContext";
import { Company } from "../../models/company";
import OnBoardingService from "../../services/onBoarding.service";
import CompanyService from "../../services/company.service";
import {
  CompanyProfile,
  OnboardingStage as OnboardingStageEnum,
  UploadValueType,
} from "../../util/enums";
import env from "../../util/env";

import OnboardingLayout from "./OnboardingLayout";
import { StepIndicatorProps } from "./StepIndicator";
import CompanyForm from "./CompanyForm";
import CompleteScreen from "./CompleteScreen";
import { Introduction } from "./Introduction";
import { StepsProps } from "./StepByStep";
import UploadCentreShipmentTracking from "./UploadCentreShipmentTracking";

import suitcaseAnimation from "../../assets/img/animations/suitcase.json";
import moneyAnimation from "../../assets/img/animations/money_billing.json";
import membersAnimation from "../../assets/img/animations/members.json";
import folderAnimation from "../../assets/img/animations/folder.json";
import MyBilling from "./MyBilling";
import Members from "./Members";
import UploadCentre from "./UploadCentre";

type OnboardingStageModel = { id?: number; stage?: OnboardingStageEnum };

const OnBoardingInner = () => {
  const { t: translate } = useTranslation();
  const { mutate: syncUser, isLoading: isLoadingSyncUser } = useActionSyncUser();
  const { currentUser, userLogout } = useAppState();
  const { useUpdateDefault } = useActionOnBoarding();

  const [isMyBillingReady, setIsMyBillingReady] = useState(false);

  const { handleValidate, company } = useOnBoardingContext(true);

  const isShipmentTracking = currentUser?.company?.profile?.initials === CompanyProfile.ST;

  const stagedFinished = isShipmentTracking
    ? OnboardingStageEnum.SHIPMENT_TRACKING_FINISHED
    : OnboardingStageEnum.FINISHED;

  const STEPS_DEFAULT: StepsProps[] = isShipmentTracking
    ? [
        {
          stage: OnboardingStageEnum.INTRODUCTION,
          label: translate("onboarding.steps.introduction"),
          active: true,
          isValid: false,
        },
        {
          stage: OnboardingStageEnum.COMPANY_DETAILS,
          label: translate("onboarding.steps.companyDetails"),
          active: false,
          isValid: false,
        },
        {
          stage: OnboardingStageEnum.SHIPMENT_TRACKING_UPLOAD_CENTRE,
          label: translate("onboarding.steps.uploadCentre"),
          active: false,
          isValid: false,
        },
        {
          stage: OnboardingStageEnum.SHIPMENT_TRACKING_COMPLETE,
          label: translate("onboarding.steps.complete"),
          active: false,
          isValid: false,
        },
      ]
    : [
        {
          stage: OnboardingStageEnum.INTRODUCTION,
          label: translate("onboarding.steps.introduction"),
          active: true,
          isValid: false,
        },
        {
          stage: OnboardingStageEnum.COMPANY_DETAILS,
          label: translate("onboarding.steps.companyDetails"),
          active: false,
          isValid: false,
        },
        {
          stage: OnboardingStageEnum.MY_BILLING,
          label: translate("onboarding.steps.myBilling"),
          active: false,
          isValid: false,
        },
        {
          stage: OnboardingStageEnum.MEMBERS,
          label: translate("onboarding.steps.members"),
          active: false,
          isValid: false,
        },
        {
          stage: OnboardingStageEnum.UPLOAD_CENTRE,
          label: translate("onboarding.steps.uploadCentre"),
          active: false,
          isValid: false,
        },
        {
          stage: OnboardingStageEnum.COMPLETE,
          label: translate("onboarding.steps.complete"),
          active: false,
          isValid: false,
        },
      ];

  const [onboardingStage, setOnboardingStage] = useState<OnboardingStageModel>();
  const [steps, setSteps] = useState<StepsProps[]>(STEPS_DEFAULT);
  const [activeStep, setActiveStep] = useState<number>(STEPS_DEFAULT[0].stage);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [uploadType, setUploadType] = useState<UploadValueType>();

  const { mutate: updateOnboardingStage, isLoading: loadingOnboardingStage } = useMutation(
    ["onboarding.stage.update.onboarding.stage"],
    ({ id, onboardingStage }: { id: number; onboardingStage: OnboardingStageModel }) =>
      OnBoardingService.update(id, onboardingStage)
  );

  const { mutate: updateUploadCenter } = useMutation(
    ["onboarding.stage.update.upload.center"],
    ({ id, onboardingStage }: { id: number; onboardingStage: OnboardingStageModel }) =>
      OnBoardingService.update(id, onboardingStage)
  );

  const { mutate: updateCompany } = useMutation(
    ["company.update"],
    ({ id, company }: { id: number; company: Company }) => CompanyService.update(id, company)
  );

  useEffect(() => {
    if (currentUser?.company?.onboardingStage) {
      const savedStage = currentUser.company.onboardingStage as OnboardingStageModel;
      setOnboardingStage(savedStage);

      const savedStageNumber = savedStage?.stage as number;
      if (savedStageNumber) {
        setActiveStep(savedStageNumber);
      } else {
        setActiveStep(STEPS_DEFAULT[0].stage);
      }
    } else if (currentUser?.companyId && !onboardingStage) {
      setActiveStep(STEPS_DEFAULT[0].stage);
    }
  }, [currentUser]);

  useEffect(() => {
    if (onboardingStage) {
      changeDefaultSteps();
    }
  }, [onboardingStage]);

  const onChangeActiveStep = (nextStage: number) => {
    if (
      nextStage &&
      onboardingStage?.stage &&
      onboardingStage?.id &&
      nextStage > (onboardingStage?.stage as number)
    ) {
      updateOnboardingStage(
        { id: onboardingStage?.id as number, onboardingStage: { stage: nextStage } },
        {
          onSuccess: () => {
            setOnboardingStage({ ...(onboardingStage || {}), stage: nextStage });
            setActiveStep(nextStage);
            syncUser();
          },
        }
      );
    } else {
      setActiveStep(nextStage);
    }
  };

  const changeDefaultSteps = () => {
    const currentStage = onboardingStage?.stage ?? 0;
    if (currentStage) {
      const stepsUpdated = [...STEPS_DEFAULT];
      stepsUpdated.forEach((s) => {
        if (s.stage < currentStage) {
          // Steps anteriores ao atual são marcados como válidos e completos (não ativos)
          s.isValid = true;
          s.active = false;
        } else if (s.stage === currentStage) {
          // Step atual é ativo e válido
          s.isValid = true;
          s.active = true;
        } else {
          // Steps futuros são desativados
          s.isValid = false;
          s.active = false;
        }
      });
      setSteps(stepsUpdated);
    }
  };

  const onUpdateOnboardingStage = (stage: OnboardingStageModel) => {
    if (stage?.id)
      updateUploadCenter(
        { id: stage?.id, onboardingStage: stage },
        {
          onSuccess: (response) => {
            if (response?.data) {
              setOnboardingStage(stage);
              syncUser();
            }
          },
        }
      );
  };

  const onComplete = () => {
    if (onboardingStage?.id) {
      updateOnboardingStage(
        { id: onboardingStage.id, onboardingStage: { stage: stagedFinished } },
        {
          onSuccess: (r) => {
            if (r?.data) {
              syncUser();
            }
          },
        }
      );
    }
  };

  const isMinimalRequiredDataFilled = (c?: Company) => {
    if (!c) return false;

    // Validar campos básicos obrigatórios
    const name = c.name?.trim();
    const registration = c.registrationNumber?.trim();
    const phone = c.phone?.trim();
    const email = c.email?.trim();

    if (!name || !registration || !phone || !email) {
      return false;
    }

    // Validar address (location) - obrigatório no onboarding
    const location = c.location;
    if (!location) return false;

    const hasAddressLine1 = location.addressLine1?.trim();
    const hasNumber = location.number?.trim();
    const hasSuburb = location.suburb?.trim();
    const hasCountry = location.country?.trim();
    const hasPostalcode = location.postalcode?.trim();

    const isAddressValid = !!(
      hasAddressLine1 &&
      hasNumber &&
      hasSuburb &&
      hasCountry &&
      hasPostalcode
    );

    return isAddressValid;
  };

  const handleNextStep = () => {
    const stepsCloned = cloneDeep(steps);
    const currentIdx = stepsCloned.findIndex((s) => s.stage === activeStep);
    const nextIdx = currentIdx + 1;

    if (!stepsCloned[currentIdx].isValid) stepsCloned[currentIdx].isValid = true;

    if (stepsCloned[nextIdx]) {
      stepsCloned[nextIdx].active = true;
      stepsCloned[nextIdx].isValid = true;
      setSteps(stepsCloned);

      const nextStage = stepsCloned[nextIdx].stage;

      // Salvar o progresso no banco quando avança para o próximo step
      if (nextStage && currentUser?.companyId) {
        const currentStage = onboardingStage?.stage ?? 0;

        // Só atualiza se o próximo stage for maior que o atual
        if (nextStage > currentStage) {
          if (onboardingStage?.id) {
            // Já existe, apenas atualiza
            updateOnboardingStage(
              { id: onboardingStage.id, onboardingStage: { stage: nextStage } },
              {
                onSuccess: () => {
                  setOnboardingStage({ ...(onboardingStage || {}), stage: nextStage });
                  setActiveStep(nextStage);
                  syncUser();
                },
                onError: () => {
                  // Em caso de erro, ainda atualiza o estado local para não bloquear o usuário
                  setActiveStep(nextStage);
                },
              }
            );
          } else {
            // Não existe, precisa criar primeiro
            OnBoardingService.create({ stage: nextStage, companyId: currentUser.companyId } as any)
              .then((response) => {
                if (response?.data) {
                  setOnboardingStage({ id: response.data.id, stage: nextStage });
                  setActiveStep(nextStage);
                  syncUser();
                } else {
                  setActiveStep(nextStage);
                }
              })
              .catch(() => {
                // Em caso de erro, ainda atualiza o estado local
                setActiveStep(nextStage);
              });
          }
        } else {
          // Se não precisa atualizar no banco, apenas atualiza o estado local
          setActiveStep(nextStage);
        }
      } else {
        // Se não tem companyId, apenas atualiza o estado local
        setActiveStep(nextStage);
      }
    }
  };

  const { mutate: updateDefault, isLoading: loadingUpdateDefault } = useUpdateDefault(() =>
    handleNextStep()
  );

  const handlePreviousStep = () => {
    const idx = steps.findIndex((s) => s.stage === activeStep) - 1;
    setActiveStep(idx <= 0 ? steps[0].stage : steps[idx].stage);
  };

  const handleNext = () => {
    switch (activeStep) {
      case OnboardingStageEnum.COMPANY_DETAILS: {
        setIsSubmitted(true);
        if (!handleValidate(company)) return;

        const companyToUpdate = company?.registrationNumber
          ? { ...company, registrationNumber: company.registrationNumber.trim() }
          : company;
        if (companyToUpdate?.id) {
          updateCompany(
            { id: companyToUpdate.id, company: companyToUpdate },
            {
              onSuccess: () => {
                // Após salvar a company, avança para o próximo step (que já salva o stage)
                handleNextStep();
              },
            }
          );
        } else {
          handleNextStep();
        }
        break;
      }
      case OnboardingStageEnum.MY_BILLING:
      case OnboardingStageEnum.MEMBERS:
        handleNextStep();
        break;
      case OnboardingStageEnum.UPLOAD_CENTRE:
        updateDefault({
          companyId: currentUser?.companyId,
          uploadType,
          branchId: currentUser?.branches?.length ? currentUser?.branches[0].id : undefined,
        });
        break;
      case OnboardingStageEnum.SHIPMENT_TRACKING_UPLOAD_CENTRE:
        if (isShipmentTracking) handleNextStep();
        break;
      default:
        handleNextStep();
        break;
    }
  };

  const handleStepClick = (stepNumber: number) => {
    const step = steps[stepNumber - 1];
    if (step && (step.isValid || step.active)) onChangeActiveStep(step.stage);
  };

  const leftSideSteps: Omit<StepIndicatorProps, "onClick">[] = useMemo(
    () =>
      steps.map((s, i) => ({
        number: i + 1,
        label: s.label,
        active: s.stage === activeStep,
        completed: Boolean(s.isValid && s.stage !== activeStep),
      })),
    [steps, activeStep]
  );

  const renderStepContent = () => {
    if (isShipmentTracking) {
      switch (activeStep) {
        case OnboardingStageEnum.INTRODUCTION:
          return <Introduction />;
        case OnboardingStageEnum.COMPANY_DETAILS:
          return <CompanyForm isSubmitted={isSubmitted} />;
        case OnboardingStageEnum.SHIPMENT_TRACKING_UPLOAD_CENTRE:
          return (
            <UploadCentreShipmentTracking
              uploadType={uploadType}
              onboardingStage={onboardingStage}
              setUploadType={setUploadType}
              onUpdateOnboardingStage={onUpdateOnboardingStage}
            />
          );
        case OnboardingStageEnum.SHIPMENT_TRACKING_COMPLETE:
          return (
            <CompleteScreen
              onComplete={onComplete}
              loading={loadingOnboardingStage || isLoadingSyncUser}
            />
          );
        default:
          return null;
      }
    }

    switch (activeStep) {
      case OnboardingStageEnum.INTRODUCTION:
        return <Introduction />;
      case OnboardingStageEnum.COMPANY_DETAILS:
        return <CompanyForm isSubmitted={isSubmitted} />;
      case OnboardingStageEnum.MY_BILLING:
        return <MyBilling onReadyChange={setIsMyBillingReady} />;
      case OnboardingStageEnum.MEMBERS:
        return <Members />;
      case OnboardingStageEnum.UPLOAD_CENTRE:
        return (
          <UploadCentre
            uploadType={uploadType}
            setUploadType={setUploadType}
            onboardingStage={onboardingStage}
            onUpdateOnboardingStage={onUpdateOnboardingStage}
          />
        );
      case OnboardingStageEnum.COMPLETE:
        return (
          <CompleteScreen
            loading={loadingOnboardingStage || isLoadingSyncUser}
            onComplete={onComplete}
          />
        );
      default:
        return null;
    }
  };

  const isCompleteStep =
    activeStep === OnboardingStageEnum.COMPLETE ||
    activeStep === OnboardingStageEnum.SHIPMENT_TRACKING_COMPLETE;

  const isMembers = activeStep === OnboardingStageEnum.MEMBERS;

  const getStepTitle = useCallback(() => {
    const currentStepConfig = steps.find((s) => s.stage === activeStep);
    return currentStepConfig?.label || "";
  }, [steps, activeStep]);

  const getStepSubtitle = useCallback(() => {
    if (
      activeStep === OnboardingStageEnum.UPLOAD_CENTRE ||
      activeStep === OnboardingStageEnum.SHIPMENT_TRACKING_UPLOAD_CENTRE
    ) {
      return translate("uploadCentreDescription", {
        defaultValue:
          "Here you can upload your own files (like inventory, staff, or service lists) or start with Shipeezi's default list. You can always update this later.",
      });
    }
    return "";
  }, [activeStep, translate]);

  const showSkipButton =
    activeStep === OnboardingStageEnum.MEMBERS ||
    (activeStep === OnboardingStageEnum.SHIPMENT_TRACKING_UPLOAD_CENTRE && isShipmentTracking);

  const showButtons = !isCompleteStep || isMembers;

  const currentStepIndex = steps.findIndex((s) => s.stage === activeStep);
  const progressValue = ((currentStepIndex + 1) / steps.length) * 100;

  const isNextDisabled =
    (activeStep === OnboardingStageEnum.COMPANY_DETAILS && !isMinimalRequiredDataFilled(company)) ||
    (activeStep === OnboardingStageEnum.MY_BILLING && !isMyBillingReady) ||
    (activeStep === OnboardingStageEnum.SHIPMENT_TRACKING_UPLOAD_CENTRE &&
      isShipmentTracking &&
      !uploadType);

  const isCompanyDetails = activeStep === OnboardingStageEnum.COMPANY_DETAILS;
  const isMyBilling = activeStep === OnboardingStageEnum.MY_BILLING;

  const rightSideOverrides = isCompanyDetails
    ? {
        contentMaxWidth: "auto" as const,
        contentScrollable: true,
        contentPaddingX: { xs: 3, md: 4, lg: 5 } as const,
        headerIconLottie: { animationData: suitcaseAnimation, size: 56 },
        buttonsMaxWidth: 1083,
        stretchButtons: true,
        backAsText: true,
      }
    : isMyBilling
    ? {
        contentMaxWidth: "auto" as const,
        contentScrollable: true,
        contentPaddingX: { xs: 3, md: 4, lg: 5 } as const,
        headerIconLottie: { animationData: moneyAnimation, size: 56 },
        buttonsMaxWidth: 1083,
        stretchButtons: true,
        backAsText: true,
      }
    : isMembers
    ? {
        contentMaxWidth: "auto" as const,
        contentScrollable: true,
        contentPaddingX: { xs: 3, md: 4, lg: 5 } as const,
        headerIconLottie: { animationData: membersAnimation, size: 56 },
        buttonsMaxWidth: 1083,
        stretchButtons: true,
        backAsText: true,
        customClassName: "members-step",
      }
    : activeStep === OnboardingStageEnum.UPLOAD_CENTRE
    ? {
        contentMaxWidth: "auto" as const,
        contentScrollable: true,
        contentPaddingX: { xs: 3, md: 4, lg: 5 } as const,
        headerIconLottie: { animationData: folderAnimation, size: 56 },
        buttonsMaxWidth: 1083,
        stretchButtons: true,
        backAsText: true,
      }
    : isCompleteStep
    ? {
        contentMaxWidth: "auto" as const,
        contentScrollable: false,
        contentPaddingX: { xs: 4, md: 6, lg: 8 } as const,
        contentBottomGutter: 0,
        customClassName: "complete-step",
        centerContent: true,
      }
    : activeStep === OnboardingStageEnum.INTRODUCTION
    ? {
        stretchButtons: true,
      }
    : {};

  return (
    <Box>
      <GlobalStyles
        styles={{
          "html, body, #root": { height: "100%", overflow: "hidden", margin: 0 },
          ".MuiToolbar-root": {
            minHeight: "0 !important",
            height: "0 !important",
            padding: "0 !important",
          },
          ".MuiToolbar-gutters": { paddingLeft: "0 !important", paddingRight: "0 !important" },
          ".onboarding-blue-label": { color: "#0055A4 !important", fontWeight: "600 !important" },
          ".members-step button[data-cy='backButton']": {
            width: "200px !important",
            minWidth: "200px !important",
            maxWidth: "200px !important",
            height: "40px !important",
            flex: "0 0 200px !important",
          },
          ".members-step button[data-cy='skipButton']": {
            width: "260px !important",
            minWidth: "260px !important",
            maxWidth: "260px !important",
            height: "40px !important",
            flex: "0 0 260px !important",
            color: "#0055A4 !important",
            borderColor: "#0055A4 !important",
          },
          ".members-step button[data-cy='skipButton']:hover": {
            backgroundColor: "#F0F7FF !important",
            borderColor: "#0055A4 !important",
          },
          ".members-step button[data-cy='nextButton']": {
            width: "260px !important",
            minWidth: "260px !important",
            maxWidth: "260px !important",
            height: "40px !important",
            flex: "0 0 260px !important",
          },
          ".members-step .upload-button": {
            border: "1px solid #475467 !important",
            color: "#475467 !important",
            backgroundColor: "transparent !important",
            "&:hover": {
              backgroundColor: "#F9FAFB !important",
              borderColor: "#475467 !important",
            },
          },
          ".members-step .add-button": {
            border: "1px solid #0055A4 !important",
            color: "#0055A4 !important",
            backgroundColor: "transparent !important",
            "&:hover": {
              backgroundColor: "#F9FAFB !important",
              borderColor: "#0055A4 !important",
            },
          },
        }}
      />

      <OnboardingLayout
        leftSideProps={{
          steps: leftSideSteps,
          onStepClick: handleStepClick,
          variant: "video",
          showIntroCopy: activeStep === OnboardingStageEnum.INTRODUCTION || isCompleteStep,
          isComplete: isCompleteStep,
        }}
        rightSideProps={{
          activeStep,
          title: !isCompleteStep ? getStepTitle() : undefined,
          subtitle: !isCompleteStep ? getStepSubtitle() : undefined,
          showHeader: !isCompleteStep,
          showButtons,
          children: renderStepContent(),
          onNext: handleNext,
          onBack: handlePreviousStep,
          onSkip: handleNextStep,
          nextDisabled: isNextDisabled,
          loading:
            activeStep === OnboardingStageEnum.COMPANY_DETAILS
              ? loadingOnboardingStage || isLoadingSyncUser
              : loadingOnboardingStage || isLoadingSyncUser || loadingUpdateDefault,
          showSkipButton,
          showDownloadButton: false,
          downloadUrl: env.LINK_TEMPLATES_ZIP,
          isFirstStep: activeStep === OnboardingStageEnum.INTRODUCTION,
          onExitOnboarding: userLogout,
          progressValue,
          loadingUpdateDefault,
          uploadType,
          updateDefault,
          ...rightSideOverrides,
        }}
      />
    </Box>
  );
};

const OnBoarding = () => (
  <OnBoardingProvider>
    <OnBoardingInner />
  </OnBoardingProvider>
);

export default OnBoarding;
